import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Mail } from 'lucide-react';
import { useAuth } from '@/contexts/useAuth';
import { useAuthError } from '@/lib/errors/useAuthError';
import Button from '@/components/ui/Button';
import OTPInput from '@/components/ui/OTPInput';
import SplitLayout from '@/components/layout/SplitLayout';
import Logo from '@/components/branding/Logo';

function maskEmail(email: string): string {
  const [local, domain] = email.split('@');
  if (!local || !domain) return email;
  const visible = local.slice(0, 2);
  return `${visible}${'*'.repeat(Math.max(local.length - 2, 2))}@${domain}`;
}

const fadeInUp = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, delay },
});

export default function VerifyOtpPage() {
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') || '';
  const navigate = useNavigate();
  const { signInWithEmail, verifyOtp } = useAuth();
  const { errorText, setFriendlyError, clearError } = useAuthError();

  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(60);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const handleVerify = useCallback(
    async (code: string) => {
      if (code.length !== 6) return;
      clearError();
      setLoading(true);
      const { error } = await verifyOtp(email, code);
      setLoading(false);
      if (error) {
        setFriendlyError(error);
      } else {
        navigate('/', { replace: true });
      }
    },
    [email, verifyOtp, navigate, clearError, setFriendlyError]
  );

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    clearError();
    const { error } = await signInWithEmail(email);
    if (error) {
      setFriendlyError(error);
    } else {
      setResendCooldown(60);
      setOtp('');
    }
  };

  return (
    <div className="bg-gradient-to-b from-cream via-cream-warm to-cream-light min-h-screen">
      <SplitLayout
        left={
          <>
            <div>
              <Logo size="md" />
            </div>
            <div>
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 text-primary mb-8">
                <Mail size={28} />
              </div>
              <h2 className="font-display italic font-light text-5xl xl:text-6xl leading-[1.05] text-text-primary mb-6">
                Check your inbox.
              </h2>
              <p className="font-sans text-lg text-text-secondary max-w-md mb-2">
                We sent a six-digit code to
              </p>
              <p className="font-sans font-semibold text-lg text-text-primary">
                {maskEmail(email)}
              </p>
            </div>
            <div className="bg-card/60 backdrop-blur-sm border border-border rounded-xl p-5 max-w-md">
              <p className="font-sans text-sm text-text-secondary leading-relaxed">
                <strong className="text-text-primary">Didn&apos;t get it?</strong> Check
                your spam folder, or wait a moment — codes can take up to a minute to
                arrive.
              </p>
            </div>
          </>
        }
      >
        <motion.button
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
          onClick={() => navigate('/auth', { replace: true })}
          className="flex items-center gap-2 text-text-secondary font-sans text-sm mb-8 hover:text-primary transition-colors cursor-pointer"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </motion.button>

        <motion.h1
          {...fadeInUp(0)}
          className="font-display italic font-light text-[44px] md:text-5xl leading-tight text-text-primary mb-3 lg:hidden"
        >
          Check your email
        </motion.h1>

        <motion.p
          {...fadeInUp(0.1)}
          className="font-sans text-lg text-text-secondary mb-10 lg:hidden"
        >
          We sent a code to{' '}
          <span className="font-semibold text-text-primary">
            {maskEmail(email)}
          </span>
        </motion.p>

        <motion.h2
          {...fadeInUp(0)}
          className="hidden lg:block font-display font-semibold text-2xl text-text-primary mb-2"
        >
          Enter your code
        </motion.h2>
        <motion.p
          {...fadeInUp(0.05)}
          className="hidden lg:block font-sans text-text-secondary mb-8"
        >
          Type the six digits we just sent.
        </motion.p>

        <motion.div {...fadeInUp(0.2)} className="mb-6">
          <OTPInput
            value={otp}
            onChange={(val) => {
              setOtp(val);
              clearError();
            }}
            onComplete={handleVerify}
            error={errorText || undefined}
            disabled={loading}
          />
        </motion.div>

        <motion.div {...fadeInUp(0.3)}>
          <Button
            fullWidth
            onClick={() => handleVerify(otp)}
            disabled={otp.length !== 6}
            loading={loading}
          >
            Verify
          </Button>
        </motion.div>

        <motion.div {...fadeInUp(0.4)} className="mt-6 text-center">
          {resendCooldown > 0 ? (
            <p className="font-sans text-[13px] text-text-muted">
              Resend code in {resendCooldown}s
            </p>
          ) : (
            <button
              onClick={handleResend}
              className="font-sans text-sm text-primary font-medium hover:underline cursor-pointer"
            >
              Resend code
            </button>
          )}
        </motion.div>
      </SplitLayout>
    </div>
  );
}
